import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { fetchSector } from "@/lib/api";
import { PageHero, Reveal, SectionEyebrow } from "@/components/Primitives";
import SEO from "@/lib/seo";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function SectorDetail() {
  const { id } = useParams();
  const [sector, setSector] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchSector(id).then(setSector).catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="container-x section-y" data-testid="sector-not-found">
        <SectionEyebrow>Not found</SectionEyebrow>
        <h1 className="mt-6 font-display text-4xl tracking-tight">This sector dossier does not exist.</h1>
        <Link to="/sectors" className="mt-6 inline-flex items-center gap-2 text-sm font-medium underline-link">
          <ArrowLeft className="h-4 w-4" /> All sectors
        </Link>
      </div>
    );
  }

  if (!sector) return <div className="container-x section-y text-sm text-muted-foreground">Loading…</div>;

  const Icon = Icons[sector.icon] || Icons.Building2;

  return (
    <div data-testid={`sector-detail-${sector.id}`}>
      <SEO
        title={`${sector.name} · Sector dossier · The Cleanability Institute`}
        description={sector.summary}
        path={`/sectors/${sector.id}`}
      />
      <section className="container-x pt-16 md:pt-24 pb-12">
        <Link to="/sectors" data-testid="back-to-sectors" className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> All sectors
        </Link>
        <div className="mt-6 flex items-center gap-4">
          <span className="h-12 w-12 rounded-xl border border-border bg-card grid place-items-center">
            <Icon className="h-5 w-5 text-[hsl(var(--accent))]" />
          </span>
          <div className="font-mono-mini text-muted-foreground">Sector dossier</div>
        </div>
        <h1 className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-balance max-w-4xl">
          {sector.name}
        </h1>
        <p className="mt-6 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">{sector.lead || sector.summary}</p>

        {sector.stats && sector.stats.length > 0 && (
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-3">
            {sector.stats.map(([v, l], i) => (
              <div key={i} data-testid={`sector-stat-${i}`} className="rounded-2xl border border-border bg-card p-6 hover-lift">
                <div className="font-display text-3xl md:text-4xl font-medium tracking-tight text-[hsl(var(--accent))]">{v}</div>
                <div className="text-xs md:text-sm text-muted-foreground mt-1">{l}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="container-x section-y border-t border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border rounded-2xl overflow-hidden border border-border">
          <DossierBlock title="Challenges" items={sector.challenges} testId="challenges" />
          <DossierBlock title="Consequences" items={sector.consequences} testId="consequences" />
          <DossierBlock title="Compliance" items={sector.compliance} testId="compliance" />
          <DossierBlock title="Design failures" items={sector.design_failures} testId="design-failures" />
          <DossierBlock title="Labour implications" items={sector.labour} testId="labour" />
          <DossierBlock title="CI focus areas" items={sector.ci_focus} testId="ci-focus" />
        </div>
      </section>

      <section className="container-x section-y border-t border-border bg-secondary/30">
        <div className="rounded-3xl border border-border bg-card p-10 md:p-16 lg:p-20">
          <SectionEyebrow>Engage</SectionEyebrow>
          <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium text-balance max-w-3xl">
            Bring the Institute into your {sector.name.toLowerCase()} portfolio.
          </h2>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="sector-cta-contact" className="inline-flex items-center gap-2 h-12 px-5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90">
              Discuss a project <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/platform#demo" data-testid="sector-cta-platform" className="inline-flex items-center gap-2 h-12 px-5 rounded-md border border-border text-sm font-medium hover:border-[hsl(var(--accent))]">
              See CleanabilityIQ
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function DossierBlock({ title, items, testId }) {
  if (!items || items.length === 0) return <div className="bg-card p-8" />;
  return (
    <div className="bg-card p-8 md:p-10" data-testid={`sector-block-${testId}`}>
      <div className="font-mono-mini text-[hsl(var(--accent))]">{title}</div>
      <ul className="mt-5 space-y-3 text-sm">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] shrink-0" />
            <span className="text-foreground/90 leading-relaxed">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
